import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { BASE_PATH } from '../../config/site'
import { searchAvailableTools } from '../../data/tools'
import { PrivacyArchitectureMark } from '../ui/PrivacyArchitectureMark'

const navItems = [
  { to: '/spending-tracker/', label: 'Spending', end: false },
  { to: '/images/', label: 'Image tools', end: false },
  { to: '/pdf/', label: 'PDF tools', end: false },
  { to: '/developer/', label: 'Developer', end: false },
  { to: '/about/', label: 'About', end: false },
  { to: '/privacy/', label: 'Privacy', end: false },
]

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchInput = useRef<HTMLInputElement>(null)
  const location = useLocation()
  const matches = searchAvailableTools(query, 6)
  const closeHeaderPanels = () => {
    setMenuOpen(false)
    setSearchOpen(false)
    setQuery('')
  }

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus()
  }, [searchOpen])

  return (
    <header className="site-header" onKeyDown={(event) => {
      if (event.key === 'Escape') {
        setSearchOpen(false)
        setQuery('')
      }
    }}>
      <div className="site-header__inner shell">
        <a className="brand" href={BASE_PATH} aria-label="StayInBrowser home" onClick={closeHeaderPanels}>
          <span className="brand__mark"><PrivacyArchitectureMark /></span>
          <span>StayInBrowser</span>
        </a>

        <div className="site-header__actions">
          <button
            className="header-search-toggle"
            type="button"
            aria-expanded={searchOpen}
            aria-controls="header-tool-search"
            onClick={() => {
              setSearchOpen((open) => !open)
              setMenuOpen(false)
            }}
          >
            <span aria-hidden="true">⌕</span>
            <span>Search</span>
          </button>

          <button
          className="nav-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
          onClick={() => {
            setMenuOpen((open) => !open)
            setSearchOpen(false)
          }}
        >
          <span />
          <span />
          </button>
        </div>

        <nav id="primary-navigation" className={`primary-nav ${menuOpen ? 'primary-nav--open' : ''}`} aria-label="Primary navigation">
          <a
            href={BASE_PATH}
            onClick={closeHeaderPanels}
            className={location.pathname === '/' ? 'primary-nav__link primary-nav__link--active' : 'primary-nav__link'}
            aria-current={location.pathname === '/' ? 'page' : undefined}
          >
            Home
          </a>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={closeHeaderPanels}
              className={({ isActive }) => (isActive ? 'primary-nav__link primary-nav__link--active' : 'primary-nav__link')}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {searchOpen && (
          <section id="header-tool-search" className="header-tool-search" aria-label="Search tools">
            <div className="header-tool-search__topline">
              <label htmlFor="header-tool-search-input">Search all local tools</label>
              <button type="button" onClick={() => { setSearchOpen(false); setQuery('') }}>Close</button>
            </div>
            <input
              ref={searchInput}
              id="header-tool-search-input"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Try “PDF”, “compress”, or “CSV”"
              autoComplete="off"
            />
            <div className="header-tool-search__results" aria-live="polite">
              {!query.trim() && <p>Start typing to find a tool.</p>}
              {query.trim() && !matches.length && <p>No tools match “{query}”.</p>}
              {matches.map((tool) => (
                <Link key={tool.id} to={tool.path} onClick={closeHeaderPanels}>
                  <span>{tool.name}</span>
                  <small>{tool.description}</small>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </header>
  )
}
